import React, { ForwardedRef, forwardRef, ReactNode, RefObject } from 'react';
import { chain } from 'react-aria/chain';
import { useObjectRef } from 'react-aria/useObjectRef';
import { useControlledState } from 'react-stately/useControlledState';
import { PressEvents } from '@react-types/shared';

import { TextFieldBase } from './TextFieldBase';
import { TextFieldRef } from '..';
import {
  SapphireTextFieldProps,
  useSapphireTextField,
} from './useSapphireTextField';
import { SapphireStyleProps, useThemeCheck } from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';

export type TextFieldProps = SapphireTextFieldProps<'input'> &
  PressEvents &
  SapphireStyleProps &
  GlobalDomAttributes & {
    prefix?: ReactNode;
    postfix?: ReactNode;
    inputRef?: RefObject<HTMLInputElement | null>;
    /**
     * @default 'lg'
     */
    size?: 'lg' | 'md';
    /**
     * Aligns the text inside the input fields without affecting the positioning of the label of the field.
     */
    alignInputRight?: boolean;

    /**
     * Places the label either above (default) or on the side of the control.
     * @default 'above'
     */
    labelPlacement?: 'side' | 'above';
  };

export const TextField = forwardRef(function TextField(
  props: TextFieldProps,
  ref: ForwardedRef<TextFieldRef>
) {
  useThemeCheck();
  const inputRef = useObjectRef<HTMLInputElement>(props.inputRef);
  // Used to trigger rerendering even if input is uncontrolled which is needed to detect when to
  // apply empty-input class

  const [_, setInputValue] = useControlledState(
    props.value,
    props.defaultValue || '',
    () => {
      // do nothing
    }
  );
  const { labelProps, inputProps, errorMessageProps, descriptionProps } =
    useSapphireTextField(
      {
        ...props,
        onChange: chain(props.onChange, setInputValue),
        inputElementType: 'input',
      },
      inputRef
    );

  return (
    <TextFieldBase
      {...props}
      labelProps={labelProps}
      inputProps={inputProps}
      errorMessageProps={errorMessageProps}
      descriptionProps={descriptionProps}
      ref={ref}
      inputRef={inputRef}
      inputElementType="input"
      prefix={props.prefix}
      postfix={props.postfix}
      alignInputRight={props.alignInputRight}
    />
  );
});
